/**
 * feat-307-redesign-tenant-mgmt — the tenant pane (/tenant_management/:tenantId).
 *
 * Everything that sat in develop's tenant card and its "Show Details" body, per
 * design/NOTES.md §6 column "1 · Tree":
 *   T1 name + description · T2 created · T3 location count (only once loaded, D4)
 *   T4/T5 client status (the caller builds it, Admin-only fail-closed)
 *   T6 Add location · T7 Prompt config · T8 View demo setup (only with a job)
 *   T15 locations table · T14 corporate managers · T11 → T12 → T13 settings cards
 *
 * T9 / T10 (Edit / Delete tenant) and Edit manager are commented out on develop
 * and are NOT rendered (spec D6).
 *
 * Layout only. Every gate is computed by TenantDashboard and passed in, and every
 * action calls back into TenantDashboard's existing handlers.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, MapPin, MessageSquare, Plus, Sparkles } from './icons';
import { Location, Manager, Tenant } from '../../../types/tenant';
import { DRIP_SUMMARY_PREFIX, dripSummaryValue } from '../drip/dripCopy';
import { effectiveLocationDrip } from '../drip/dripSettings';
import { cls, fmtDate } from './styles';
import { locationPath } from './routes';

export interface TenantPaneProps {
  tenant: Tenant;
  detailsLoaded: boolean;
  detailsLoading: boolean;
  /** Fail-closed Admin gate: Live chip + Drip column. */
  showClientStatus: boolean;
  /** T4 + T5, already gated by the caller (null for non-Admin). */
  clientStatus: React.ReactNode;
  /** T11 → T12 → T13, in that order, built by the caller. */
  settingsCards: React.ReactNode;
  canCreateLocation: boolean;
  onAddLocation: () => void;
  onPromptConfig: () => void;
  hasDemoJob: boolean;
  onViewDemoSetup: () => void;
  canCreateCorporateManager: boolean;
  onAddCorporateManager: () => void;
  canDeleteManager: (managerId: string) => boolean;
  onDeactivateCorporateManager: (manager: Manager) => void;
}

function LocationsTable({
  tenant,
  locations,
  showDrip,
}: {
  tenant: Tenant;
  locations: Location[];
  showDrip: boolean;
}) {
  return (
    <div className={`${cls.card} overflow-x-auto`} data-testid="tenant-locations-table">
      <table className="min-w-full">
        <thead>
          <tr>
            <th className={cls.th}>Location</th>
            <th className={cls.th}>Auth</th>
            <th className={cls.th}>Timezone</th>
            <th className={cls.th}>Go-live</th>
            {showDrip && <th className={cls.th}>Drip</th>}
            <th className={cls.th}>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {locations.map((l) => {
            const eff = effectiveLocationDrip(l, tenant);
            return (
              <tr key={l.id} className="border-t border-softBorder hover:bg-cream">
                <td className={cls.td}>
                  <Link
                    to={locationPath(tenant.tenantId, l.id)}
                    className="font-semibold text-ink no-underline hover:text-coral"
                    data-testid={`tenant-location-link-${l.id}`}
                  >
                    {l.name}
                  </Link>
                </td>
                <td className={`${cls.td} text-inkMuted whitespace-nowrap`}>{l.authMethod === 'pin' ? 'PIN' : 'Phone OTP'}</td>
                <td className={`${cls.td} text-inkMuted whitespace-nowrap`}>{l.timezone}</td>
                <td className={`${cls.td} whitespace-nowrap ${l.bespokeGoLiveDate ? '' : 'text-inkMuted'}`}>
                  {l.bespokeGoLiveDate || 'Not yet live'}
                </td>
                {showDrip && (
                  <td className={`${cls.td} whitespace-nowrap text-inkMuted`}>
                    <span className="sr-only">{DRIP_SUMMARY_PREFIX}</span>
                    {dripSummaryValue(eff.enabled, eff.rate, eff.overridden)}
                  </td>
                )}
                <td className={`${cls.td} text-right text-inkMuted`}>
                  <ChevronRight className="w-4 h-4 inline" aria-hidden="true" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function TenantPane({
  tenant,
  detailsLoaded,
  detailsLoading,
  showClientStatus,
  clientStatus,
  settingsCards,
  canCreateLocation,
  onAddLocation,
  onPromptConfig,
  hasDemoJob,
  onViewDemoSetup,
  canCreateCorporateManager,
  onAddCorporateManager,
  canDeleteManager,
  onDeactivateCorporateManager,
}: TenantPaneProps) {
  const locations = tenant.locations;
  const corporateManagers = tenant.corporateManagers ?? [];
  const n = locations.length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl space-y-5" data-testid={`tenant-pane-${tenant.tenantId}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-[16rem] flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl lg:text-3xl font-semibold leading-tight text-ink break-words">
              {tenant.tenantName}
            </h2>
            {showClientStatus && tenant.isLive && (
              <span className="inline-flex items-center justify-center min-w-[64px] rounded-full bg-success-light text-success-on text-[11px] font-bold px-2 py-0.5">
                Live
              </span>
            )}
          </div>
          {tenant.description && <p className="text-sm text-ink mt-1 max-w-3xl">{tenant.description}</p>}
          <p className={`${cls.muted} mt-1`} data-testid="tenant-meta">
            Created: {new Date(tenant.createdAt).toLocaleDateString()}
            {/* D4 — never print a count for locations that have not loaded. */}
            {detailsLoaded && ` · ${n} location${n === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onPromptConfig} className={cls.btnSecondary}>
            <MessageSquare className="w-4 h-4" aria-hidden="true" />
            Prompt config
            <ExternalLink className="w-3 h-3 text-inkMuted" aria-hidden="true" />
          </button>
          {hasDemoJob && (
            <button
              type="button"
              onClick={onViewDemoSetup}
              className={cls.btnSecondary}
              title="View the latest demo setup job for this tenant"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              View demo setup
            </button>
          )}
          {canCreateLocation && (
            <button type="button" onClick={onAddLocation} className={cls.btnPrimary}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add location
            </button>
          )}
        </div>
      </div>

      {clientStatus}

      <section aria-labelledby="tenant-locations-heading">
        <h3 id="tenant-locations-heading" className={`${cls.h2} !text-base mb-2`}>
          Locations{' '}
          {detailsLoaded && <span className="text-inkMuted font-sans text-sm font-semibold">{n}</span>}
        </h3>
        {!detailsLoaded ? (
          <div className={`${cls.card} p-6 text-center ${cls.muted}`}>{detailsLoading ? 'Loading...' : ''}</div>
        ) : n === 0 ? (
          <div className={`${cls.card} p-8 text-center`}>
            <div className="mx-auto w-10 h-10 rounded-full bg-cream-dark flex items-center justify-center text-inkMuted">
              <MapPin className="w-5 h-5" aria-hidden="true" />
            </div>
            <p className="mt-3 font-semibold text-ink">No locations found.</p>
          </div>
        ) : (
          <LocationsTable tenant={tenant} locations={locations} showDrip={showClientStatus} />
        )}
      </section>

      <section className={`${cls.card} p-4`} aria-labelledby="corporate-managers-heading">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h3 id="corporate-managers-heading" className={`${cls.h2} !text-base`}>
            Corporate managers{' '}
            {detailsLoaded && (
              <span className="text-inkMuted font-sans text-sm font-semibold">{corporateManagers.length}</span>
            )}
          </h3>
          {canCreateCorporateManager && (
            <button type="button" onClick={onAddCorporateManager} className={cls.btnSecondary}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add corporate manager
            </button>
          )}
        </div>
        {!detailsLoaded ? (
          <p className={cls.muted}>{detailsLoading ? 'Loading...' : ''}</p>
        ) : corporateManagers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={cls.th}>Name</th>
                  <th className={cls.th}>Email</th>
                  <th className={cls.th}>Created</th>
                  <th className={`${cls.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {corporateManagers.map((manager) => (
                  <tr key={manager.id} className="border-t border-softBorder">
                    <td className={`${cls.td} font-semibold`}>
                      {manager.firstName} {manager.lastName}
                    </td>
                    <td className={cls.td}>{manager.email}</td>
                    <td className={`${cls.td} text-inkMuted`}>{fmtDate(manager.createdAt)}</td>
                    <td className={`${cls.td} text-right`}>
                      {canDeleteManager(manager.id) && (
                        <button
                          type="button"
                          onClick={() => onDeactivateCorporateManager(manager)}
                          title="Revoke login and remove from manager list"
                          className={cls.actDanger}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={cls.muted}>No corporate managers found.</p>
        )}
      </section>

      {settingsCards}
    </div>
  );
}
